import React, { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { ArrowLeftIcon, EyeIcon } from '@heroicons/react/24/outline'
import toast from 'react-hot-toast'
import Card from '../../components/common/Card'
import Button from '../../components/common/Button'
import Input from '../../components/common/Input'
import LoadingSpinner from '../../components/common/LoadingSpinner'
import BlogImage from '../../components/common/BlogImage'
import RichTextEditor from '../../components/common/RichTextEditor'
import RichTextContent from '../../components/common/RichTextContent'
import { blogsApi } from '../../services/api'
import { Blog } from '../../types'
import { resolveMediaUrl } from '../../utils/media'

const slugify = (value: string) => value
  .toLowerCase()
  .trim()
  .replace(/[^a-z0-9]+/g, '-')
  .replace(/^-+|-+$/g, '')

const BlogEditorPage: React.FC = () => {
  const navigate = useNavigate()
  const { id } = useParams()
  const isEdit = Boolean(id)

  const [loading, setLoading] = useState(isEdit)
  const [saving, setSaving] = useState(false)
  const [showPreview, setShowPreview] = useState(false)
  const [slugTouched, setSlugTouched] = useState(false)
  const [existingImage, setExistingImage] = useState<string | null>(null)
  const [removeImage, setRemoveImage] = useState(false)
  const [imageFile, setImageFile] = useState<File | null>(null)
  const [imagePreview, setImagePreview] = useState<string | null>(null)

  const [form, setForm] = useState({
    title: '',
    slug: '',
    short_description: '',
    content: '',
    category: '',
    tags: '',
    author: '',
    read_time: '',
    meta_title: '',
    meta_description: '',
    status: 'draft' as 'draft' | 'published',
  })

  useEffect(() => {
    if (!isEdit || !id) return
    setLoading(true)
    blogsApi.adminGetBlog(Number(id))
      .then((blog: Blog) => {
        setForm({
          title: blog.title || '',
          slug: blog.slug || '',
          short_description: blog.short_description || blog.summary || '',
          content: blog.content || '',
          category: blog.category || '',
          tags: blog.tags || '',
          author: blog.author || '',
          read_time: blog.read_time || '',
          meta_title: blog.meta_title || '',
          meta_description: blog.meta_description || '',
          status: blog.status === 'published' ? 'published' : 'draft',
        })
        setExistingImage(blog.featured_image || null)
        setImagePreview(resolveMediaUrl(blog.featured_image) || null)
      })
      .catch(() => {
        toast.error('Failed to load blog')
        navigate('/admin/blogs')
      })
      .finally(() => setLoading(false))
  }, [id, isEdit])

  useEffect(() => {
    if (slugTouched) return
    setForm(prev => ({ ...prev, slug: slugify(prev.title) }))
  }, [form.title, slugTouched])

  const setField = (field: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const value = e.target.value
    setForm(prev => ({ ...prev, [field]: value }))
  }

  const onSlugChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSlugTouched(true)
    setForm(prev => ({ ...prev, slug: slugify(e.target.value) }))
  }

  const onSelectImage = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null
    setImageFile(file)
    setRemoveImage(false)
    if (!file) {
      setImagePreview(resolveMediaUrl(existingImage) || null)
      return
    }
    const url = URL.createObjectURL(file)
    setImagePreview(url)
  }

  const isValid = useMemo(() => {
    return Boolean(form.title.trim() && form.slug.trim() && form.short_description.trim() && form.content.trim())
  }, [form])

  const save = async (targetStatus?: 'draft' | 'published') => {
    if (!isValid) {
      toast.error('Please fill title, slug, short description, and content.')
      return
    }
    setSaving(true)
    const payload = {
      ...form,
      slug: slugify(form.slug),
      status: targetStatus || form.status,
      featured_image_file: imageFile,
      remove_featured_image: removeImage,
    }

    try {
      if (isEdit && id) {
        await blogsApi.adminUpdateBlog(Number(id), payload)
        toast.success('Blog updated successfully')
      } else {
        await blogsApi.adminCreateBlog(payload)
        toast.success('Blog created successfully')
      }
      navigate('/admin/blogs')
    } catch (err: any) {
      toast.error(err?.response?.data?.detail || 'Unable to save blog')
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <div className="h-64 flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <Link to="/admin/blogs" className="inline-flex items-center gap-1.5 text-sm text-gray-400 hover:text-white">
            <ArrowLeftIcon className="w-4 h-4" />
            Back to Blog Management
          </Link>
          <h1 className="text-3xl font-bold text-white mt-2">{isEdit ? 'Edit Blog' : 'Create Blog'}</h1>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <Button variant="outline" onClick={() => setShowPreview(true)}>
            <EyeIcon className="w-4 h-4 mr-2" />
            Preview
          </Button>
          <Button variant="ghost" disabled={saving} onClick={() => save('draft')}>Save Draft</Button>
          <Button disabled={saving} onClick={() => save('published')}>{isEdit ? 'Update & Publish' : 'Publish'}</Button>
        </div>
      </div>

      <Card>
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
          <div className="xl:col-span-2 space-y-4">
            <Input label="Title *" value={form.title} onChange={setField('title')} placeholder="Enter blog title" />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input label="Slug *" value={form.slug} onChange={onSlugChange} placeholder="auto-generated-slug" />
              <Input label="Category" value={form.category} onChange={setField('category')} placeholder="Market, Education, Strategy" />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-200 mb-2">Short Description *</label>
              <textarea
                rows={3}
                value={form.short_description}
                onChange={setField('short_description')}
                className="w-full px-3 py-2 bg-dark-800 border border-dark-600 text-gray-100 rounded-md placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500"
                placeholder="Write a concise excerpt for listing cards and SEO"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-200 mb-2">Content *</label>
              <RichTextEditor
                value={form.content}
                onChange={(content) => setForm(prev => ({ ...prev, content }))}
                placeholder="Write your article…"
              />
            </div>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-200 mb-2">Featured Image</label>
              <input
                type="file"
                accept="image/png,image/jpeg,image/webp"
                onChange={onSelectImage}
                className="w-full text-sm text-gray-300 file:mr-3 file:px-3 file:py-2 file:rounded-md file:border-0 file:bg-dark-700 file:text-gray-200 hover:file:bg-dark-600"
              />
              <div className="mt-3 rounded-lg border border-dark-600 w-full h-44 bg-dark-800 overflow-hidden">
                <BlogImage
                  src={imagePreview}
                  alt="Featured image preview"
                  className="w-full h-full object-cover"
                  fallbackClassName="w-full h-full flex items-center justify-center text-xs text-gray-500"
                  fallbackText="No image selected"
                />
              </div>
              {(existingImage || imageFile) && (
                <label className="mt-2 inline-flex items-center gap-2 text-xs text-gray-400">
                  <input
                    type="checkbox"
                    checked={removeImage}
                    onChange={(e) => {
                      setRemoveImage(e.target.checked)
                      if (e.target.checked) {
                        setImageFile(null)
                        setImagePreview(null)
                      } else {
                        setImagePreview(resolveMediaUrl(existingImage) || null)
                      }
                    }}
                  />
                  Remove featured image
                </label>
              )}
            </div>

            <Input label="Author" value={form.author} onChange={setField('author')} placeholder="Author name" />
            <Input label="Read Time" value={form.read_time} onChange={setField('read_time')} placeholder="5 min read" />
            <Input label="Tags" value={form.tags} onChange={setField('tags')} placeholder="comma,separated,tags" />

            <Input label="Meta Title" value={form.meta_title} onChange={setField('meta_title')} placeholder="SEO meta title" />
            <div>
              <label className="block text-sm font-medium text-gray-200 mb-2">Meta Description</label>
              <textarea
                rows={4}
                value={form.meta_description}
                onChange={setField('meta_description')}
                className="w-full px-3 py-2 bg-dark-800 border border-dark-600 text-gray-100 rounded-md placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500"
                placeholder="SEO meta description"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-200 mb-2">Status</label>
              <select
                value={form.status}
                onChange={setField('status')}
                className="w-full px-3 py-2 bg-dark-800 border border-dark-600 text-gray-100 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                <option value="draft">Draft</option>
                <option value="published">Published</option>
              </select>
            </div>

            <div className="grid grid-cols-2 gap-2 pt-2">
              <Button variant="outline" disabled={saving} onClick={() => save('draft')}>Save Draft</Button>
              <Button disabled={saving} onClick={() => save('published')}>Publish</Button>
            </div>
          </div>
        </div>
      </Card>

      {showPreview && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={() => setShowPreview(false)}>
          <div className="w-full max-w-4xl max-h-[90vh] overflow-auto rounded-2xl border border-dark-700 bg-dark-900" onClick={(e) => e.stopPropagation()}>
            <div className="p-5 border-b border-dark-700 flex items-center justify-between">
              <h2 className="text-xl font-bold text-white">Preview</h2>
              <button type="button" className="text-gray-400 hover:text-white" onClick={() => setShowPreview(false)}>Close</button>
            </div>
            <div className="p-6">
              <div className="w-full h-64 rounded-lg border border-dark-700 bg-dark-800 overflow-hidden">
                <BlogImage
                  src={imagePreview}
                  alt={form.title || 'Blog image preview'}
                  className="w-full h-full object-cover"
                  fallbackClassName="w-full h-full flex items-center justify-center text-sm text-gray-500"
                  fallbackText="No image selected"
                />
              </div>
              <h1 className="mt-4 text-3xl font-bold text-white">{form.title || 'Untitled Blog'}</h1>
              <p className="mt-2 text-gray-300">{form.short_description || 'No description.'}</p>
              <div className="mt-2 text-xs text-gray-500">{form.author || 'AnalyzerView Desk'} · {form.read_time || '5 min read'}</div>
              <RichTextContent html={form.content || '<p>No content added.</p>'} className="mt-6 prose prose-invert prose-p:text-gray-300 prose-headings:text-white" />
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default BlogEditorPage
